import { useCallback } from "react";
import toast from "react-hot-toast";

export interface UseCopyToClipboardOptions {
  /** コピー成功時のトーストメッセージ。既定 "クリップボードにコピーしました"。 */
  successMessage?: string;
  /** コピー失敗時のトーストメッセージ。既定 "コピーに失敗しました"。 */
  errorMessage?: string;
  /** 成功時のコールバック。 */
  onSuccess?: () => void;
  /** 失敗時のコールバック。 */
  onError?: (error: Error) => void;
  /** true ならトーストを内部で呼び出さない（呼び出し側で制御したいとき）。 */
  silent?: boolean;
}

export interface UseCopyToClipboardReturn {
  /** 指定文字列をクリップボードにコピーする。 */
  copy: (text: string) => Promise<void>;
}

/**
 * クリップボードへのコピーを担う hook。旧 CopyButton コンポーネントの
 * ロジック部分をコンポーネントから切り出したもの。
 *
 * - 既定で `toast.success` / `toast.error` を呼ぶ。表示には呼び出し側で
 *   `<Toaster />` を mount しておく必要がある（プロジェクト画面は
 *   `ProjectWrapper.jsx` がグローバル mount 済み）。
 * - 旧 `<CopyButton text="..." />` の見た目は、`<IconButton icon="content_copy"
 *   onClick={() => copy(text)} />` で再現する。
 */
export const useCopyToClipboard = (
  options: UseCopyToClipboardOptions = {},
): UseCopyToClipboardReturn => {
  const {
    successMessage = "クリップボードにコピーしました",
    errorMessage = "コピーに失敗しました",
    onSuccess,
    onError,
    silent = false,
  } = options;

  const copy = useCallback(
    async (text: string): Promise<void> => {
      try {
        await navigator.clipboard.writeText(text);
        if (!silent) toast.success(successMessage);
        onSuccess?.();
      } catch (err) {
        const error = err instanceof Error ? err : new Error(errorMessage);
        if (!silent) toast.error(errorMessage);
        onError?.(error);
      }
    },
    [successMessage, errorMessage, onSuccess, onError, silent],
  );

  return { copy };
};

export default useCopyToClipboard;
